'use client'
import { Box, Stack, Typography } from '@mui/material'
import { useLocale } from 'next-intl'
import React, { useCallback, useState } from 'react'

import CouponImage from '@/src/assets/svgs/CouponImage'
import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer'
import OfferDetailClient from '@/src/components/pages/offerDetail/OfferDetailClient'
import { getOfferSlugFromLink } from '@/src/utils/offerDetailUtils'

interface Coupon {
    id: string
    heading: string
    description: string
    targetLink: string
}

interface CouponSectionProps {
    coupons: Coupon[]
    isMobile?: boolean
}

const CouponSection: React.FC<CouponSectionProps> = ({ coupons, isMobile = false }) => {
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [activeOfferSlug, setActiveOfferSlug] = useState<string | null>(null)

    const handleCouponClick = useCallback((targetLink: string) => {
        if (isMobile) {
            const slug = getOfferSlugFromLink(targetLink)
            if (slug) {
                setActiveOfferSlug(slug)
                setDrawerOpen(true)
            }
        } else {
            window.open(targetLink, '_blank', 'noopener,noreferrer')
        }
    }, [isMobile])

    const handleDrawerClose = useCallback(() => {
        setDrawerOpen(false)
        setTimeout(() => setActiveOfferSlug(null), 300)
    }, [])

    if (!coupons || coupons.length === 0) {
        return null
    }

    return (
        <Box sx={{ pb: 1, pt: { sm: 1, md: 0 } }}>
            <Stack
                direction="row"
                spacing={1.5}
                sx={{
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': {
                        display: 'none'
                    },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                }}
            >
                {coupons.map(coupon => {
                    const hasLink = coupon.targetLink && coupon.targetLink.trim() !== ''

                    const couponContent = (
                        <>
                            <Box sx={{ mr: 1.5, flexShrink: 0 }}>
                                <CouponImage sx={{ fontSize: '20px' }} />
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: 'rgb(17, 166, 112)',
                                        lineHeight: 1.375
                                    }}
                                >
                                    {coupon.description}
                                </Typography>
                            </Box>
                        </>
                    )

                    const commonStyles = {
                        width: '280px',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '12px',
                        px: 1.5,
                        py: 1,
                        bgcolor: 'rgb(236, 248, 244)',
                        flexShrink: 0
                    }

                    return hasLink ? (
                        <Box
                            key={coupon.id}
                            onClick={() => handleCouponClick(coupon.targetLink)}
                            sx={{
                                ...commonStyles,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: 'rgb(226, 238, 234)',
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            {couponContent}
                        </Box>
                    ) : (
                        <Box
                            key={coupon.id}
                            sx={commonStyles}
                        >
                            {couponContent}
                        </Box>
                    )
                })}
            </Stack>

            {/* Mobile bottom sheet for offer details */}
            {isMobile && (
                <SwipeableDrawerWithBackNavigation
                    open={drawerOpen}
                    onClose={handleDrawerClose}
                    sx={{ zIndex: 1400 }}
                >
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                        {activeOfferSlug && (
                            <OfferDetailClient offerSlug={activeOfferSlug} />
                        )}
                    </Box>
                </SwipeableDrawerWithBackNavigation>
            )}
        </Box>
    )
}

export { CouponSection }
export type { Coupon, CouponSectionProps }
